<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <main class="main-content">
      <!-- Verification banner (hidden on print) -->
      <section class="page-hero no-print">
        <div class="container">
          <nav class="breadcrumb-nav">
            <a href="/" class="breadcrumb-link">Home</a>
            <span class="breadcrumb-separator">/</span>
            <span class="breadcrumb-current">Verify Land Ownership Certificate</span>
          </nav>
          <h1 class="page-title">Verify Land Ownership Certificate</h1>
          <p class="page-subtitle">Public verification for a Land Ownership Certificate. Scan the QR on any printed certificate to land here.</p>
        </div>
      </section>

      <!-- Search box. Visible until a valid certificate is loaded so the
           user can (re)type an ID after a bad lookup without reloading. -->
      <section v-if="!valid && !loading" class="content-section no-print">
        <div class="container" style="max-width:720px;">
          <div class="verify-card">
            <label class="verify-label">Property ID or UPN</label>
            <div class="verify-row">
              <input type="text" class="verify-input" placeholder="e.g. 50722 or ATSV"
                     v-model="query" @keyup.enter="lookup" :disabled="loading">
              <button class="verify-btn" @click="lookup" :disabled="loading || !query.trim()">
                <span v-if="loading">Verifying…</span>
                <span v-else>Verify</span>
              </button>
            </div>
            <small class="verify-hint">Tip: scan the QR code on a printed certificate to verify it instantly.</small>
            <p v-if="notFoundMsg" class="verify-error">{{ notFoundMsg }}</p>
          </div>
        </div>
      </section>

      <!-- Verdict banner (no Print button here — see floating FAB below) -->
      <section v-if="loaded" class="content-section no-print" style="padding-top:24px;padding-bottom:0;">
        <div class="container" style="max-width:920px;">
          <div class="verdict-banner" :class="valid ? 'ok' : 'bad'">
            <div class="verdict-icon">{{ valid ? '✓' : '!' }}</div>
            <div class="verdict-body">
              <h3>{{ valid ? 'Certificate is VALID' : 'Certificate NOT FOUND' }}</h3>
              <p v-if="valid">
                Serial <strong>{{ certificate.certificateNumber || '—' }}</strong> ·
                Issued to <strong>{{ certificate.ownerNames || '—' }}</strong> ·
                <span v-if="issuedAtLabel">Issued {{ issuedAtLabel }}</span>
              </p>
              <p v-else>
                We couldn't find a certificate for <code>{{ lastQueried }}</code>. Make sure the property ID / UPN is correct.
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- Floating Print / Save FAB — top-right, above the cert flow but
           below the fixed header. Never clashes with page-flow content and
           auto-hides on print. -->
      <button v-if="valid" @click="printCert" class="print-fab no-print" type="button">
        <span aria-hidden="true">🖨</span>
        <span class="print-fab-label">Print / Save as PDF</span>
      </button>

      <!-- Certificate render (visible AND used as print target) -->
      <section v-if="valid" class="cert-section">
        <div class="cert-outer">
          <div class="page-container"
               :style="{
                 width:'8.268in', height:'11.693in', position:'relative',
                 margin:'0 auto', padding:'0', background:'#fff',
                 backgroundImage: 'url(' + backgroundUrl + ')',
                 backgroundSize: '100% 100%', backgroundRepeat: 'no-repeat'
               }">

            <img v-if="qrLink" :src="qrLink" alt="qr"
                 style="position:absolute;top:0.55in;left:0.55in;width:1.1in;height:1.1in;">

            <div style="position:absolute;top:0.6in;right:0.6in;text-align:right;">
              <div style="font:700 15pt 'Aldrich',sans-serif;color:#000;letter-spacing:1px;">
                Serial NO. {{ certificate.certificateNumber || '—' }}
              </div>
              <div style="margin-top:6px;width:22px;height:22px;border:1.5px solid #000;display:inline-block;"></div>
            </div>

            <div style="position:absolute;top:1.8in;left:0;right:0;text-align:center;">
              <img v-if="county.logo" :src="county.logo" alt="county-coat"
                   style="height:1.55in;max-width:2in;object-fit:contain;">
            </div>

            <div style="position:absolute;top:3.7in;left:1.1in;right:1.1in;text-align:center;">
              <div style="font:800 22pt 'Montserrat',sans-serif;color:#111;text-transform:uppercase;letter-spacing:0.5px;line-height:1.15;">
                County Government of {{ (county.countyName || '').toUpperCase() }}
              </div>
              <div style="font:700 12pt 'Montserrat',sans-serif;color:#111;text-transform:uppercase;margin-top:6px;letter-spacing:0.5px;">
                Department of Lands &amp; Physical Planning
              </div>
            </div>

            <!-- Cursive title. `white-space:nowrap` keeps it on ONE line so
                 "Ownership" never wraps into the paragraph below. Slightly
                 smaller font (32pt) than the RMS-MAIN template because the
                 self-service viewport tends to render Norican a touch wider. -->
            <div style="position:absolute;top:4.85in;left:0.6in;right:0.6in;text-align:center;">
              <span style="font:400 32pt Norican, cursive;color:#111;white-space:nowrap;display:inline-block;">Certificate Of Land Ownership</span>
            </div>

            <div style="position:absolute;top:5.9in;left:1.1in;right:1.1in;text-align:center;
                        font:400 12pt 'Fira Sans',sans-serif;color:#111;line-height:1.55;">
              This is to certify that
              <strong>{{ certificate.ownerNames || '—' }}
                <span v-if="certificate.ownerPhoneNo">({{ certificate.ownerPhoneNo }})</span>
              </strong>
              of ID number <strong>{{ certificate.ownerID || '—' }}</strong>
              is now the registered owner of Surveyed/Unsurveyed Plot/Farm No
              <strong>{{ lrLabel }}</strong>
              to be held on {{ tenureLabel }} interest, measuring approximately
              <strong>{{ sizeLabel }}</strong>
              situated in <strong>{{ locationLabel }}</strong>.
            </div>

            <div style="position:absolute;top:7.6in;left:1.1in;right:1.1in;text-align:center;
                        font:400 11pt 'Fira Sans',sans-serif;color:#111;">
              Given Under the hand and seal of {{ county.countyName || '—' }} land Registry on
              <strong>{{ formatDateTime(certificate.issuedAt || certificate.last_certificate_issued_at) }}</strong>
            </div>

            <div style="position:absolute;top:8.5in;left:1.1in;right:3.4in;font:400 11pt 'Fira Sans',sans-serif;color:#111;">
              <div style="font-weight:700;">Principle Land Administrator:</div>
              <div>{{ signatories.principalName || '—' }}</div>
              <div v-if="signatories.principalIdNo">ID:{{ signatories.principalIdNo }}</div>
              <div style="margin-top:6px;">
                signature: <span v-if="!signatories.principalSignatureUrl">.................................</span>
                <img v-else :src="signatories.principalSignatureUrl" alt="sig"
                     style="height:0.5in;width:auto;vertical-align:middle;">
              </div>
            </div>
            <div style="position:absolute;top:9.2in;left:1.1in;right:3.4in;font:400 11pt 'Fira Sans',sans-serif;color:#111;">
              <div style="font-weight:700;">Chief Officer:</div>
              <div>{{ signatories.chiefName || '—' }}</div>
              <div v-if="signatories.chiefIdNo">ID:{{ signatories.chiefIdNo }}</div>
              <div style="margin-top:6px;">
                signature: <span v-if="!signatories.chiefSignatureUrl">.................................</span>
                <img v-else :src="signatories.chiefSignatureUrl" alt="sig"
                     style="height:0.5in;width:auto;vertical-align:middle;">
              </div>
            </div>

            <img v-if="poweredByLogo" :src="poweredByLogo" alt="powered"
                 style="position:absolute;bottom:1.15in;left:1.1in;height:0.5in;">

            <div style="position:absolute;bottom:1.05in;right:1.1in;text-align:center;">
              <img v-if="rubberStampUrl || approvedSealUrl" :src="rubberStampUrl || approvedSealUrl" alt="seal"
                   style="height:1.35in;width:auto;">
              <div style="margin-top:4px;background:#c9d5e6;color:#1c4a94;font:700 10pt 'Fira Sans',sans-serif;
                          padding:4px 12px;letter-spacing:2px;border-radius:3px;display:inline-block;">
                APPROVED
              </div>
            </div>

            <div style="position:absolute;bottom:0.7in;left:1.1in;right:1.1in;
                        font:400 10pt 'Fira Sans',sans-serif;color:#111;">
              <strong>NB:</strong> This Certificate does not exempt the holder from obeying existing laws.
            </div>

            <div style="position:absolute;bottom:0.35in;left:1.1in;right:1.1in;
                        display:flex;justify-content:space-between;gap:0.5in;
                        font:400 8pt 'Fira Sans',sans-serif;color:#111;">
              <span>
                <strong>County Government of {{ county.countyName }},</strong> |
                {{ county.address || 'P.O Box —, ' + (county.countyName || '') }}
              </span>
              <span>
                <strong>Contacts, T:</strong> {{ county.contact || '—' }}
                <strong> T: E:</strong> {{ county.email || '—' }}
              </span>
            </div>
          </div>
        </div>
      </section>
    </main>

    <FooterComponent class="no-print" />
  </div>
</template>

<script>
/* eslint-disable */
// Public verification + print/download of a Land Ownership Certificate.
// Deep-linked from the QR code on every printed certificate (RMS-MAIN
// OwnershipCertificate.vue encodes <selfService>/#/verify-loc/<propertyId>).
// Renders exactly the same certificate the operator sees, plus a
// verification banner and a Print / Save-as-PDF button.
import { execute, landRate, biller, base } from '@/api';
import HeaderComponent from '@/components/HeaderComponent.vue';
import FooterComponent from '@/components/FooterComponent.vue';

export default {
  name: 'VerifyOwnershipCertificate',
  components: { HeaderComponent, FooterComponent },
  data() {
    return {
      query: '',
      lastQueried: '',
      loading: false,
      loaded: false,
      valid: false,
      notFoundMsg: '',

      backgroundUrl: '',
      defaultBackground: base + '/logo/loc-background.png',
      approvedSealUrl:   base + '/logo/seal.png',
      rubberStampUrl:    '',
      poweredByLogo:     base + '/logo/poweredbylogo.png',
      qrLink: '',

      certificate: {},
      county: {},
      signatories: {
        principalName: '', principalIdNo: '', principalSignatureUrl: '',
        chiefName: '',     chiefIdNo: '',     chiefSignatureUrl: '',
      },
    };
  },
  computed: {
    lrLabel() {
      const c = this.certificate || {};
      return c.LRNumber || c.paymodeCode || c.plotNo || '—';
    },
    tenureLabel() {
      const t = (this.certificate.tenure || '').toString().toLowerCase();
      if (t.includes('freeh')) return 'Freehold';
      if (t.includes('lease')) return 'Leasehold';
      return 'Leasehold';
    },
    sizeLabel() {
      const c = this.certificate || {};
      const size = c.percelSize || c.parcelSize || c.size || '';
      if (!size) return '—';
      return /acre|ha|hect/i.test(String(size)) ? size : (size + ' Acres');
    },
    locationLabel() {
      const c = this.certificate || {};
      const parts = [
        c.wardName    ? c.wardName + ' Ward' : '',
        c.subCountyName ? c.subCountyName + ' Sub County' : '',
        this.county.countyName ? this.county.countyName + ' County' : '',
      ].filter(Boolean);
      return parts.join(', ') || '—';
    },
    issuedAtLabel() {
      const s = this.certificate.issuedAt || this.certificate.last_certificate_issued_at;
      return s ? this.formatDateTime(s) : '';
    },
  },
  mounted() {
    this.loadModuleSettings();
    // QR deep-link: /verify-loc/<propertyId>
    const fromParam = this.$route && this.$route.params && this.$route.params.propertyId;
    if (fromParam) {
      this.query = decodeURIComponent(fromParam);
      this.lookup();
    }
  },
  methods: {
    printCert() { window.print(); },
    formatDateTime(s) {
      if (!s) return '—';
      const d = new Date(String(s).replace(' ', 'T'));
      if (isNaN(d.getTime())) return s;
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const hh = d.getHours();
      const mm = ('0' + d.getMinutes()).slice(-2);
      const ampm = hh >= 12 ? 'PM' : 'AM';
      const hh12 = ((hh + 11) % 12) + 1;
      return `${('0'+d.getDate()).slice(-2)} ${months[d.getMonth()]} ${d.getFullYear()} ${hh12}:${mm} ${ampm}`;
    },
    loadModuleSettings() {
      const fd = new FormData();
      fd.append('function', 'getModuleSettings');
      fd.append('module', 'loc');
      execute(fd, biller).then(res => {
        const d = res && res.data && res.data.data ? res.data.data : {};
        this.backgroundUrl = d.permitBackgroundUrl || this.defaultBackground;
        const s = d.settings || {};
        const val = (row) => (row ? (row.settingValue ?? row.value ?? '') : '');
        this.signatories.principalName          = val(s.principalName);
        this.signatories.principalIdNo          = val(s.principalIdNo);
        this.signatories.principalSignatureUrl  = d.principalSignatureUrl || '';
        this.signatories.chiefName              = val(s.chiefName);
        this.signatories.chiefIdNo              = val(s.chiefIdNo);
        this.signatories.chiefSignatureUrl      = d.chiefSignatureUrl || '';
        this.rubberStampUrl = d.rubberStampUrl ? (d.rubberStampUrl + '?v=' + Date.now()) : '';
      }).catch(() => { this.backgroundUrl = this.defaultBackground; });
    },
    lookup() {
      const id = String(this.query || '').trim();
      if (!id) return;
      this.notFoundMsg = '';
      this.loading = true;
      const fd = new FormData();
      fd.append('function', 'getOwnershipCertificate');
      if (/^\d+$/.test(id)) fd.append('propertyId',  id);
      else                  fd.append('paymodeCode', id);
      execute(fd, landRate).then(res => {
        this.loading = false;
        this.loaded  = true;
        this.lastQueried = id;
        if (res && res.data && res.data.success) {
          this.valid       = true;
          this.certificate = res.data.data.certificate || {};
          this.county      = res.data.data.county || {};
          if (this.county.poweredByLogo) this.poweredByLogo = this.county.poweredByLogo;
          // Regenerate QR pointing back at this same page so a photocopy of
          // the printout still verifies (helps auditors chain-verify copies).
          const selfSvc = this.county.selfService ? String(this.county.selfService).replace(/\/$/, '') : '';
          const propId  = this.certificate.propertyId || this.certificate.paymodeCode || '';
          if (selfSvc && propId) {
            this.qrLink = 'https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=' +
                          encodeURIComponent(selfSvc + '/#/verify-loc/' + propId);
          }
        } else {
          this.valid       = false;
          this.certificate = {};
          this.notFoundMsg = (res && res.data && res.data.message) || 'Certificate not found';
        }
      }).catch(() => {
        this.loading = false;
        this.loaded  = true;
        this.valid   = false;
        this.notFoundMsg = 'Network error — please retry.';
      });
    },
  },
};
</script>

<style scoped>
.page-wrapper { min-height: 100vh; display: flex; flex-direction: column; background: #f8fafc; }
.main-content { flex: 1; padding-top: 72px; }

.page-hero { background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%); padding: 48px 0; color: #fff; }
.container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
.breadcrumb-nav { display:flex; gap:8px; margin-bottom:16px; font-size:14px; }
.breadcrumb-link { color: rgba(255,255,255,0.7); text-decoration:none; }
.breadcrumb-separator { color: rgba(255,255,255,0.4); }
.breadcrumb-current { color:#fff; }
.page-title { font-size: 30px; font-weight: 800; margin-bottom: 6px; }
.page-subtitle { opacity: 0.9; font-size: 15px; }

.content-section { padding: 40px 0; }

.verify-card { background:#fff; border-radius:16px; padding:24px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); }
.verify-label { display:block; font-weight:600; color:#1e293b; margin-bottom:10px; }
.verify-row { display:flex; gap:10px; }
.verify-input { flex:1; padding:12px 14px; border:2px solid #e2e8f0; border-radius:10px; font-size:15px; }
.verify-btn { padding:12px 20px; background:#0f4c81; color:#fff; border:0; border-radius:10px; font-weight:600; cursor:pointer; }
.verify-btn:disabled { opacity:0.6; cursor:not-allowed; }
.verify-hint { display:block; margin-top:10px; color:#64748b; }
.verify-error { color:#dc2626; margin-top:12px; font-weight:500; }

.verdict-banner { display:flex; gap:16px; align-items:center; padding:20px 24px; border-radius:14px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); }
.verdict-banner.ok  { background:#d1fae5; }
.verdict-banner.bad { background:#fee2e2; }
.verdict-icon { width:52px; height:52px; border-radius:50%; background:#fff; display:flex; align-items:center; justify-content:center; font-size:28px; font-weight:700; flex-shrink:0; }
.verdict-banner.ok  .verdict-icon { color:#059669; }
.verdict-banner.bad .verdict-icon { color:#dc2626; }
.verdict-body { flex:1; }
.verdict-body h3 { margin:0 0 4px 0; font-size:18px; font-weight:700; color:#1e293b; }
.verdict-body p  { margin:0; font-size:14px; color:#334155; }

/* Floating action button. Fixed to top-right so it never collides with
   the certificate content, the HomePage floating chat FAB, or any other
   page-flow widget. Sits BELOW the fixed header (headerHeight ~72px +
   small gap) so the header always wins. */
.print-fab {
  position: fixed;
  top: 88px;
  right: 24px;
  z-index: 1050;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #0f4c81;
  color: #fff;
  border: 0;
  border-radius: 999px;
  padding: 12px 20px;
  font: 600 14px/1.2 var(--font-body, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif);
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(15,76,129,0.35);
  white-space: nowrap;
  transition: background .15s ease, transform .1s ease;
}
.print-fab:hover  { background:#0a3459; }
.print-fab:active { transform: translateY(1px); }
.print-fab-label { display:inline; }
@media (max-width: 480px) {
  /* On very small screens collapse to just the icon so we don't cover
     the "Save as PDF" text on the verdict banner. */
  .print-fab { padding: 12px 14px; }
  .print-fab-label { display: none; }
}

.cert-section { padding: 24px 0 60px; background:#eaeaea; }
.cert-outer   { background:#eaeaea; }

@media print {
  .no-print { display: none !important; }
  .main-content { padding-top: 0 !important; }
  .cert-section { background: #fff !important; padding: 0 !important; }
  .cert-outer   { background: #fff !important; }
  .page-wrapper { background: #fff !important; }
}
</style>
